import React, { FC, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  AuthError,
  AuthProvider,
  GoogleAuthProvider,
  OAuthCredential,
  OAuthProvider,
  fetchSignInMethodsForEmail,
  linkWithCredential,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
} from "firebase/auth";
import { useAuth, firebaseAuth } from "./authContext";
import "./Login.css";

// Official Google "G" mark.
const GoogleIcon: FC = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
    />
    <path
      fill="#34A853"
      d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
    />
    <path
      fill="#FBBC05"
      d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
    />
    <path
      fill="#EA4335"
      d="M9 3.58c1.321 0 2.508.454 3.44 1.346l2.582-2.581C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
    />
  </svg>
);

// Official Microsoft four-square mark.
const MicrosoftIcon: FC = () => (
  <svg width="20" height="20" viewBox="0 0 21 21" aria-hidden="true">
    <rect x="1" y="1" width="9" height="9" fill="#F25022" />
    <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
    <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
    <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
  </svg>
);

type ProviderKey = "google" | "microsoft";

const SsoButton: FC<{
  provider: ProviderKey;
  label: string;
  disabled?: boolean;
  onClick: () => void;
}> = ({ provider, label, disabled, onClick }) => (
  <button
    type="button"
    className={`sso-button sso-button--${provider}`}
    disabled={disabled}
    onClick={onClick}
  >
    <span className="sso-icon">
      {provider === "google" ? <GoogleIcon /> : <MicrosoftIcon />}
    </span>
    <span>{label}</span>
  </button>
);

const buildMicrosoftProvider = (): AuthProvider => {
  const provider = new OAuthProvider("microsoft.com");
  provider.setCustomParameters({ prompt: "select_account" });
  return provider;
};

const buildGoogleProvider = (): AuthProvider => new GoogleAuthProvider();

// Returns a provider instance for an existing sign-in method, or null if we
// can't sign in with it programmatically (e.g. password).
const providerForMethod = (method: string): AuthProvider | null => {
  switch (method) {
    case "google.com":
      return buildGoogleProvider();
    case "microsoft.com":
      return buildMicrosoftProvider();
    default:
      return null;
  }
};

// The pending credential is attached to the original error; the static helper
// differs by provider, so try both.
const credentialFromError = (error: AuthError): OAuthCredential | null =>
  OAuthProvider.credentialFromError(error) ??
  GoogleAuthProvider.credentialFromError(error);

type PendingLink = {
  credential: OAuthCredential | null;
  provider: AuthProvider;
  providerKey: ProviderKey;
  providerLabel: string;
  email: string;
};

const PROVIDER_LABELS: Record<string, string> = {
  "google.com": "Google",
  "microsoft.com": "Microsoft",
};

export const Login: FC = () => {
  const { currentUser, token } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pendingLink, setPendingLink] = useState<PendingLink | null>(null);

  // Once Firebase has a user, hand off to the router; PrivateRoute/Home decide
  // what to show (dashboard, or an email-verification prompt for password users).
  if (currentUser || token) {
    return <Navigate replace to="/" />;
  }

  const handleOAuthError = (err: AuthError) => {
    if (err.code === "auth/account-exists-with-different-credential") {
      const pendingCredential = credentialFromError(err);
      const conflictEmail = (err.customData?.email as string | undefined) ?? "";

      // Work out which provider already owns this email so we can sign in with
      // it and link the new one. fetchSignInMethodsForEmail can return [] when
      // email-enumeration protection is on, so fall back to the verified
      // provider carried on the error itself.
      const verified = (err.customData as any)?._tokenResponse
        ?.verifiedProvider as string[] | undefined;

      void (async () => {
        let existingMethod: string | undefined;
        try {
          if (conflictEmail) {
            const methods = await fetchSignInMethodsForEmail(
              firebaseAuth,
              conflictEmail
            );
            existingMethod = methods.find((m) => m !== "password");
          }
        } catch {
          // ignore - fall back to the verified provider below
        }
        existingMethod = existingMethod ?? verified?.find((m) => m !== "password");

        const existingProvider = existingMethod
          ? providerForMethod(existingMethod)
          : null;

        if (!existingProvider || !existingMethod) {
          setError(
            `This email already has an account using ${
              existingMethod ?? "a different sign-in method"
            }. Please sign in with that method.`
          );
          return;
        }

        // Defer the second sign-in to a user click so the browser doesn't block
        // the popup (popups must be opened from a user gesture).
        setPendingLink({
          credential: pendingCredential,
          provider: existingProvider,
          providerKey: existingMethod === "microsoft.com" ? "microsoft" : "google",
          providerLabel: PROVIDER_LABELS[existingMethod] ?? existingMethod,
          email: conflictEmail,
        });
      })();
      return;
    }

    if (
      err.code === "auth/popup-closed-by-user" ||
      err.code === "auth/cancelled-popup-request"
    ) {
      return; // user dismissed the popup - nothing to report
    }

    setError(err.message);
  };

  const handleOAuth = async (provider: AuthProvider) => {
    setError(null);
    setInfo(null);
    setPendingLink(null);
    setBusy(true);
    try {
      await signInWithPopup(firebaseAuth, provider);
      // onAuthStateChanged (authContext) handles backend auth + redirect.
    } catch (err) {
      handleOAuthError(err as AuthError);
    } finally {
      setBusy(false);
    }
  };

  const handleLinkAccounts = async () => {
    if (!pendingLink) return;
    setError(null);
    setBusy(true);
    try {
      const result = await signInWithPopup(firebaseAuth, pendingLink.provider);
      if (pendingLink.credential) {
        await linkWithCredential(result.user, pendingLink.credential);
      }
      setPendingLink(null);
      // Signed in (and linked); the currentUser redirect above takes over.
    } catch (err) {
      const e = err as AuthError;
      if (
        e.code !== "auth/popup-closed-by-user" &&
        e.code !== "auth/cancelled-popup-request"
      ) {
        setError(e.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setPendingLink(null);
    setBusy(true);
    try {
      await signInWithEmailAndPassword(firebaseAuth, email, password);
    } catch (err) {
      setError((err as AuthError).message);
    } finally {
      setBusy(false);
    }
  };

  const handlePasswordReset = async () => {
    setError(null);
    setInfo(null);
    if (!email) {
      setError("Enter your email address above, then click reset.");
      return;
    }
    try {
      await sendPasswordResetEmail(firebaseAuth, email);
      setInfo("Password reset email sent. Check your inbox.");
    } catch (err) {
      setError((err as AuthError).message);
    }
  };

  return (
    <div className="container" style={{ maxWidth: 400 }}>
      <h1 className="h3 mb-4 mt-3 text-center">Sign in to SharePark</h1>

      {error && <div className="alert alert-danger">{error}</div>}
      {info && <div className="alert alert-info">{info}</div>}

      {pendingLink ? (
        <div className="alert alert-warning">
          <p>
            An account already exists for <strong>{pendingLink.email}</strong>{" "}
            using {pendingLink.providerLabel}. Sign in with{" "}
            {pendingLink.providerLabel} to link your accounts.
          </p>
          <div className="sso-buttons">
            <SsoButton
              provider={pendingLink.providerKey}
              label={`Sign in with ${pendingLink.providerLabel} & link`}
              disabled={busy}
              onClick={handleLinkAccounts}
            />
            <button
              type="button"
              className="btn btn-link"
              disabled={busy}
              onClick={() => setPendingLink(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="sso-buttons mb-3">
            <SsoButton
              provider="microsoft"
              label="Sign in with Microsoft"
              disabled={busy}
              onClick={() => handleOAuth(buildMicrosoftProvider())}
            />
            <SsoButton
              provider="google"
              label="Sign in with Google"
              disabled={busy}
              onClick={() => handleOAuth(buildGoogleProvider())}
            />
          </div>

          <div className="text-center text-muted mb-3">or</div>

          <form onSubmit={handleEmailSignIn}>
            <div className="mb-3">
              <label className="form-label" htmlFor="login-email">
                Email
              </label>
              <input
                id="login-email"
                type="email"
                className="form-control"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
              />
            </div>
            <div className="mb-3">
              <label className="form-label" htmlFor="login-password">
                Password
              </label>
              <input
                id="login-password"
                type="password"
                className="form-control"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
            </div>
            <div className="d-grid">
              <button type="submit" className="btn btn-primary" disabled={busy}>
                Sign in
              </button>
            </div>
          </form>

          <div className="text-center mt-3">
            <button
              type="button"
              className="btn btn-link"
              disabled={busy}
              onClick={handlePasswordReset}
            >
              Forgot password?
            </button>
          </div>
        </>
      )}
    </div>
  );
};
